import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * SafeHTML — Production-grade HTML sanitization using DOMPurify.
 * 
 * DOMPurify is the industry standard for XSS prevention. It:
 * - Strips all malicious code (scripts, event handlers, etc.)
 * - Only allows safe HTML tags and attributes
 * - Is maintained and audited by security researchers
 * - Runs in O(n) time regardless of nesting depth
 */

// Configure DOMPurify with sensible defaults
const purifyConfig = {
  ALLOWED_TAGS: [
    'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
    'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'img', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'hr', 'pre', 'code', 'blockquote', 'sub', 'sup',
    'font', 'small', 'u', 's', 'del', 'ins', 'dl', 'dt', 'dd',
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'width', 'height',
    'class', 'id', 'style', 'target', 'rel', 'colspan', 'rowspan',
    'align', 'color', 'size', 'face',
  ],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

/**
 * Sanitize an HTML string. Returns safe HTML.
 * @param {string} dirty - Potentially unsafe HTML string
 * @returns {string} Sanitized HTML string
 */
export function sanitizeHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  return DOMPurify.sanitize(dirty, purifyConfig);
}

/**
 * React component that renders sanitized HTML content.
 * Usage: <SafeHTML html={someHtmlString} />
 * Optionally pass a tag name: <SafeHTML html={...} tag="span" />
 */
export default function SafeHTML({ html, tag: Tag = 'div', ...props }) {
  const sanitized = useMemo(() => sanitizeHtml(html), [html]);

  if (!sanitized) return null;

  return <Tag {...props} dangerouslySetInnerHTML={{ __html: sanitized }} />;
}
